/**
 * The All Events roll-up, in ONE place.
 *
 * The case timeline's "All Events" tab and the three reports (Client, Technical,
 * Executive) are meant to show the same thing: an analyst signs off on what is on
 * screen, and the report is what leaves the building. They were built from two
 * different code paths, so they drifted — on the reference case the screen showed
 * 45 rows and the report stream held 5,474, with the non-interactive feed missing
 * from the report ENTIRELY (see mergeNonInteractiveIpList for the same gap in the
 * report's IP list, closed in v1.153.0).
 *
 * 🔴 This function is applied at the SAME POINT on both sides — immediately after
 * per-type tagging, before enrichment and before the hide-pills — so parity is
 * STRUCTURAL rather than something two copies happen to agree on. TimelineView's
 * `combinedEvents` and eventUtils' `combineAndTagEvents` both call it and neither
 * does any rolling of its own. If you add a roll-up, add it here.
 *
 * 🔴 It is deliberately NOT applied to anything that COUNTS. A count of sign-ins
 * must be the number of sign-ins, not the number of periods they group into, so
 * `combineAndTagEvents` leaves it off by default and the Executive Report's fact
 * pack keeps its per-event stream for metrics, accessed items and mailbox detail
 * while rolling only the rows it RENDERS. Rolling a counting surface would also
 * break the server's reconciliation, which bounds every metric by the pinned run
 * blob's own workload totals.
 *
 * Message-trace collapse is NOT here: each caller does it in its own phase 1 (TimelineView
 * via collapseMessageTrace, storyGenerator and the Executive pack via
 * collapseMessageTraceInStream, immediately before this function). Running it again over
 * already-collapsed rows would re-group rows that are already one-per-message.
 *
 * ⚠️ This header used to say "both sides already do it ... and they already agree". That was
 * true of storyGenerator and FALSE of the Executive Report pack, which did neither -- so its
 * row count ran ahead of the screen by the recipient fan-out (483 vs 482 on
 * IR-20260818-Controller-001), breaking the very equality v1.171.0 shipped. Fixed in
 * v1.175.0. If a THIRD caller appears, it collapses too, or it inherits the same defect.
 */
import { buildAccessPeriodsInStream } from './accessPeriods.js';
import { buildDirectoryActionsInStream } from './directoryActions.js';
import { nonInteractiveAppLabel } from './nonInteractiveRollup.js';
import { buildTeamsMeetingsInStream } from './teamsMeetings.js';
import { CASE_ALERT_EVENT_TYPE, CASE_ALERT_SOURCE_NAME } from './caseAlerts.js';

/**
 * Roll a tagged, mixed event stream into the shape the All Events tab renders.
 *
 * @param {Array<object>} stream Rows already carrying `_eventType`.
 * @returns {Array<object>} Same order, with sign-ins and non-interactive
 *   authentications collapsed into access periods and directory rows into actions.
 */
export function applyAllEventsRollups(stream) {
  let out = buildAccessPeriodsInStream(stream, 'SignIn');

  // The non-interactive feed arrives pre-aggregated at (session, IP, hour) grain, so a
  // period's constituent rows each carry their own authentication count. Without
  // `countOf` a 511-authentication day would be reported as the number of HOURS.
  out = buildAccessPeriodsInStream(out, 'NonInteractiveSignIn', {
    countOf: (e) => Number(e.EventCount) || 1,
  });

  out = buildDirectoryActionsInStream(out);

  // Teams meeting records: Microsoft writes one per ATTENDEE into the organizer's log, so a
  // meeting the case user organized arrived as a row per colleague, each under the case
  // user's name. One row per meeting; every attendee is kept on it (utils/teamsMeetings.js).
  out = buildTeamsMeetingsInStream(out);

  // The Application column label is stamped AFTER the roll, never before: a period row
  // is spread from its FIRST constituent, so a label computed pre-roll names only the
  // first hour's applications and silently under-reports a multi-day period.
  // nonInteractiveAppLabel reads `_periodApps` when it is present for exactly this.
  return out.map((e) => (
    e && e._eventType === 'NonInteractiveSignIn'
      ? { ...e, _niAppLabel: nonInteractiveAppLabel(e) }
      : e
  ));
}

/**
 * How many SOURCE events a row stands for.
 *
 * 🔴 Any surface that states a COUNT must go through this. A report sentence like
 * "the attacker signed in 38 times" is derived from the same stream the report
 * renders, and after the roll-up those 38 sign-ins are ONE row — so a bare
 * `.filter(...).length` silently became "signed in 1 time". The period carries its
 * own constituent count, so the true number is still exact; it just is not the row
 * count any more.
 *
 * Deliberately does NOT expand a collapsed message-trace row: trace records are
 * PER-RECIPIENT, and "N emails were sent" means N messages, not N recipient rows
 * (v1.152.0 fixed that figure in the other direction — do not undo it).
 */
export function underlyingEventCount(evt) {
  const e = evt || {};
  if (e._period) return Number(e._periodRowCount) || 1;
  if (e._action) return Number(e._actionRowCount) || 1;
  if (e._meeting) return Number(e._meetingRowCount) || 1;
  return 1;
}

/**
 * The Application label for a single sign-in row.
 *
 * Falls back to a URL-shaped `ApplicationId` before "Unknown": an AiTM phishing-kit
 * landing app (e.g. `https://portal.microsoftonline.com.orgid.com/`) has no friendly
 * `ApplicationName`, so the row would otherwise read "Unknown" and hide the one value
 * an analyst most needs to see. Shared by rowAppLabel (the Application column) and both
 * generateDescription copies so the column and the "SignIn … on '…'" description agree.
 */
export function signInApplicationLabel(evt) {
  const e = evt || {};
  if (e.ApplicationName) return e.ApplicationName;
  const appId = String(e.ApplicationId || '');
  if (/^https?:\/\//i.test(appId)) return appId;
  return 'Unknown';
}

/**
 * The Application-column label for a row, wherever it is rendered.
 *
 * 🔴 A period row is spread from its FIRST constituent, so `ApplicationName` on it names
 * ONE application out of however many the period spans — and the row's own description
 * names the dominant one, so the two contradicted each other in the same row: a period
 * reading "Windows Sign In" while its description said "via Office365 Shell WCSS-Client
 * ×17". A reader cannot tell which is the fact. The period's own distinct set is.
 */
export function rowAppLabel(evt) {
  const e = evt || {};
  if (e._eventType === 'NonInteractiveSignIn') return e._niAppLabel || nonInteractiveAppLabel(e);
  if (e._meeting) return 'Microsoft Teams';
  if (e._period) {
    const names = rowAppNames(e);
    if (names.length === 1) return names[0];
    if (names.length === 2) return names.join(', ');
    if (names.length > 2) return `${names[0]} +${names.length - 1} more`;
  }
  if (e._eventType === 'SignIn') return signInApplicationLabel(e);
  // A portal alert's Operation is the rule name, which the description already shows.
  if (e._eventType === CASE_ALERT_EVENT_TYPE) return e.AppDisplayName || CASE_ALERT_SOURCE_NAME;
  const op = e.Operation;
  if (op && typeof op === 'object') return op.localizedValue || op.value || 'Unknown';
  return op || 'Unknown';
}

/** Sum of `underlyingEventCount` over a set of rows. */
export function underlyingEventTotal(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((n, e) => n + underlyingEventCount(e), 0);
}

/**
 * Every application name a row touched.
 *
 * A period row is spread from its FIRST constituent, so `ApplicationName` on it names
 * one application out of however many the period spans. `_periodApps` is the period's
 * own distinct set.
 */
export function rowAppNames(evt) {
  const e = evt || {};
  if (Array.isArray(e._periodApps) && e._periodApps.length) {
    return e._periodApps.map((a) => a && a.name).filter(Boolean);
  }
  const one = e.ApplicationName || '';
  return one ? [one] : [];
}
